import { DatePicker as MuiDatePicker, DatePickerProps as MuiDatePickerProps } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { Calendar } from 'lucide-react';
import React from 'react';

import { Controller, UseControllerProps } from 'react-hook-form';
import { getComputedLabel } from 'utils/label-required-option';

type CustomDatePickerProps = {
  label: string;
  dark?: boolean;
  error?: boolean;
  helperText?: string;
  hiddenLabel?: boolean;
  fullWidth?: boolean;
  viewByMonth?: boolean;
  minDate?: string | Dayjs | null;
  maxDate?: string | Dayjs | null;
  onChange?: (date: Dayjs) => void;
};

type BaseDatePickerProps = Omit<MuiDatePickerProps, 'value' | 'onChange' | 'minDate' | 'maxDate'> & CustomDatePickerProps;

type DatePickerProps =
  | (BaseDatePickerProps & { controller: UseControllerProps<any>; value?: any })
  | (BaseDatePickerProps & { controller?: undefined; value: any });

const DatePicker: React.FC<DatePickerProps> = ({
  controller,
  value,
  onChange,
  label,
  error = false,
  helperText,
  dark = false,
  hiddenLabel = false,
  fullWidth = false,
  viewByMonth = false,
  minDate,
  maxDate,
  views,
  slotProps: consumerSlotProps,
  slots: consumerSlots,
  ...props
}) => {
  const backgroundClass = dark ? 'Mui-backgroundDark' : 'Mui-backgroundLight';
  const resolvedMinDate = minDate != null ? dayjs(minDate) : undefined;
  const resolvedMaxDate = maxDate != null ? dayjs(maxDate) : undefined;

  const baseSlotProps = {
    ...consumerSlotProps,
    textField: {
      fullWidth: fullWidth,
      className: backgroundClass,
      InputLabelProps: {
        className: backgroundClass,
      },
      InputProps: {
        className: backgroundClass,
      },
      FormHelperTextProps: {
        className: backgroundClass,
      },
    },
    openPickerIcon: {
      className: backgroundClass,
    },
  };

  const mergedSlots = {
    ...consumerSlots,
    openPickerIcon: ({ ownerState, ...iconProps }: any) => <Calendar {...iconProps} />,
  };

  return controller ? (
    <Controller
      {...controller}
      render={({ field, fieldState }) => {
        const computedLabel = getComputedLabel(label, controller);
        const hasError = !!fieldState.invalid || error;
        const currentHelperText = fieldState.error?.message ?? helperText;
        return (
          <MuiDatePicker
            {...props}
            className={backgroundClass}
            label={hiddenLabel ? '' : computedLabel}
            value={field.value ? dayjs(field.value) : null}
            views={views ?? (viewByMonth ? ['year', 'month'] : ['year', 'month', 'day'])}
            minDate={resolvedMinDate}
            maxDate={resolvedMaxDate}
            onChange={(date, context) => {
              field.onChange(date?.isValid() ? date.format('YYYY-MM-DD') : date ?? null);
              if (date?.isValid() && onChange) onChange(date);
            }}
            slots={mergedSlots}
            slotProps={{
              ...baseSlotProps,
              textField: {
                ...baseSlotProps.textField,
                error: hasError,
                helperText: currentHelperText,
              },
            }}
          />
        );
      }}
    />
  ) : (
    <MuiDatePicker
      {...props}
      onChange={(date) => {
        if (date?.isValid() && onChange) onChange(date);
      }}
      className={backgroundClass}
      label={hiddenLabel ? '' : label}
      value={value ? dayjs(value) : null}
      views={views ?? (viewByMonth ? ['year', 'month'] : ['year', 'month', 'day'])}
      minDate={resolvedMinDate}
      maxDate={resolvedMaxDate}
      slots={mergedSlots}
      slotProps={{
        ...baseSlotProps,
        textField: {
          ...baseSlotProps.textField,
          error: error,
          helperText: helperText ? helperText : '',
        },
      }}
    />
  );
};

export default React.memo(DatePicker);
